/**
 * Склонения для интерфейса МСК Газобетона.
 *
 * Единственная утилита склонений для газобетонных компонентов: сводки, карточек
 * машин и окна дополнения. Заводить вторую систему нельзя — расхождения в текстах
 * соседних блоков уже были.
 */

// Выбор формы по числу: [1, 2-4, 5+].
const pluralForm = (count, [one, few, many]) => {
  const n = Math.abs(Math.round(Number(count) || 0));
  const lastTwo = n % 100;
  const lastOne = n % 10;

  if (lastTwo >= 11 && lastTwo <= 14) {
    return many;
  }
  if (lastOne === 1) {
    return one;
  }
  if (lastOne >= 2 && lastOne <= 4) {
    return few;
  }
  return many;
};

export const vehiclesWord = (count) => pluralForm(count, ['машина', 'машины', 'машин']);

export const palletsWord = (count) => pluralForm(count, ['поддон', 'поддона', 'поддонов']);

/** «1 рейс» / «2 рейса» / «5 рейсов» — для строк доставки в клиентском КП. */
export const tripsWord = (count) => pluralForm(count, ['рейс', 'рейса', 'рейсов']);

/** «1 поддономесто» / «2 поддономеста» / «5 поддономест». */
export const palletPlacesWord = (count) => pluralForm(count, ['поддономесто', 'поддономеста', 'поддономест']);

/** «1 день» / «2 дня» / «5 дней» — для возраста цены в сравнении производств. */
export const daysWord = (count) => pluralForm(count, ['день', 'дня', 'дней']);

// Род типа транспорта нужен для «шаланда полная» и «манипулятор полный».
// Определяется по окончанию названия, а не отдельным списком: женский род —
// названия на «-а»/«-я» (шаланда, фура), остальные — мужской (манипулятор, длинномер).
const isFeminineVehicle = (type) => /[ая]$/.test(String(type || '').trim().toLowerCase());

/** «шаланда загружена» / «манипулятор загружен». */
export const loadedWord = (type) => (isFeminineVehicle(type) ? 'загружена' : 'загружен');

/** «1 полная» / «2 полные» / «9 полных» — с учётом рода типа транспорта. */
export const fullWord = (count, type) => (
  isFeminineVehicle(type)
    ? pluralForm(count, ['полная', 'полные', 'полных'])
    : pluralForm(count, ['полный', 'полные', 'полных'])
);

export const partialWord = (count, type) => (
  isFeminineVehicle(type)
    ? pluralForm(count, ['неполная', 'неполные', 'неполных'])
    : pluralForm(count, ['неполный', 'неполные', 'неполных'])
);

// Названия типов транспорта во множественном числе. Формы задаются окончаниями,
// потому что тип приходит из расчёта строкой и списка допустимых значений у нас нет.
export const vehicleTypeWord = (type, count) => {
  const name = String(type || '').trim();
  if (!name) {
    return '';
  }
  const lower = name.toLowerCase();

  if (isFeminineVehicle(lower)) {
    // шаланда → шаланды → шаланд
    const stem = lower.slice(0, -1);
    return pluralForm(count, [lower, `${stem}ы`, stem]);
  }

  // манипулятор → манипулятора → манипуляторов
  return pluralForm(count, [lower, `${lower}а`, `${lower}ов`]);
};

/**
 * Состав рейсов по типам транспорта с группировкой одинаковых типов.
 * «2 шаланды + 1 манипулятор», но никогда «1 шаланда + 1 шаланда».
 * @param {Array<{type: string, count: number}>} vehicles
 */
export const vehicleTypeBreakdown = (vehicles) => {
  if (!Array.isArray(vehicles) || vehicles.length === 0) {
    return null;
  }

  const totals = new Map();
  for (const vehicle of vehicles) {
    const type = String(vehicle?.type || '').trim();
    const count = Math.max(Math.round(Number(vehicle?.count) || 0), 0);
    if (!type || count === 0) {
      continue;
    }
    totals.set(type, (totals.get(type) || 0) + count);
  }

  if (totals.size === 0) {
    return null;
  }

  return Array.from(totals.entries())
    .map(([type, count]) => `${count} ${vehicleTypeWord(type, count)}`)
    .join(' + ');
};

export default {
  vehiclesWord,
  palletsWord,
  loadedWord,
  fullWord,
  partialWord,
  vehicleTypeWord,
  vehicleTypeBreakdown,
};
